import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import { Activity, Flame, TrendingUp, Tv } from "lucide-react";
import { countryToFlag } from "../lib/countryFlag";
import LiveStreamModal from "./LiveStreamModal";
import { LiveBetButtons } from "./LiveBetButtons";
import FollowTeamButton from "./FollowTeamButton";
import { LiveOnBetwayBadge } from "./LiveOnBetwayBadge";
import TVBadge from "./TVBadge";
import { formatLiveMinute } from "../lib/liveMinute";

const StatusPill = ({ status, minute, minuteExtra }) => {
  const isHT = status === "HT";
  const isLive = ["1H", "2H", "ET"].includes(status);
  const inStoppage = isLive && typeof minuteExtra === "number" && minuteExtra > 0;
  const label = isHT
    ? "HT"
    : isLive
    ? (formatLiveMinute(minute, minuteExtra, status) || "'")
    : status;
  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider ${
        inStoppage
          ? "bg-amber-500/20 text-amber-300 border border-amber-500/50"
          : isLive
          ? "bg-destructive/15 text-destructive border border-destructive/40"
          : isHT
          ? "bg-warning/15 text-warning border border-warning/40"
          : "bg-muted text-muted-foreground border border-border"
      }`}
      title={inStoppage ? `Stoppage time — ${minuteExtra} added minute${minuteExtra === 1 ? "" : "s"}` : undefined}
    >
      {isLive && <span className={`w-1.5 h-1.5 rounded-full ${inStoppage ? "bg-amber-300" : "bg-destructive"} animate-pulse`} />}
      {label}
    </div>
  );
};

const LMIBar = ({ value, label }) => {
  const pct = Math.round((value || 0) * 100);
  return (
    <div className="flex flex-col gap-1 flex-1 min-w-0">
      <div className="flex justify-between items-center text-[11px]">
        <span className="text-muted-foreground font-medium truncate">{label}</span>
        <span className="font-tabular font-bold text-foreground">{pct}</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            pct >= 60 ? "bg-success" : pct >= 40 ? "bg-warning" : "bg-muted-foreground/60"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

export const NowPlayingSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [streamFor, setStreamFor] = useState(null); // {fixture_id, home, away}
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    const fetchLive = () => {
      axios
        .get(`${API}/live/now-playing`)
        .then((res) => { if (mounted) { setData(res.data); setLoading(false); } })
        .catch(() => mounted && setLoading(false));
    };
    fetchLive();
    const id = setInterval(fetchLive, 60000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  if (loading) {
    return (
      <div className="nd-card p-6 text-muted-foreground" data-testid="now-playing-loading">
        Loading live matches…
      </div>
    );
  }

  const matches = data?.matches || [];

  if (matches.length === 0) {
    return (
      <div
        className="nd-card p-8 text-center"
        data-testid="now-playing-empty"
      >
        <Activity className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
        <h3 className="font-display text-lg font-semibold">No matches in play right now</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Live LMI scores and triggers will appear here when matches kick off.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="now-playing-section">
      <div className="flex items-center justify-between">
        <span className="eyebrow" data-testid="now-playing-count">
          {matches.length} live
        </span>
        {data?.fetched_at && (
          <span className="text-[11px] text-muted-foreground font-tabular">
            {new Date(data.fetched_at).toLocaleTimeString()}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {matches.slice(0, 9).map((m) => (
          <article
            key={m.fixture_id}
            data-testid={`live-match-${m.fixture_id}`}
            className="nd-card nd-card-hover p-4 relative group"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground truncate font-medium inline-flex items-center gap-1">
                <span aria-hidden>{countryToFlag(m.league?.country)}</span>
                {m.league.name}
              </span>
              <div className="flex items-center gap-1.5 shrink-0">
                <TVBadge matchId={m.fixture_id} compact />
                <StatusPill status={m.status} minute={m.minute} minuteExtra={m.minute_extra} />
              </div>
            </div>

            {m.betway_available && (
              <div className="mb-3 -mt-1">
                <LiveOnBetwayBadge
                  onWatch={() =>
                    setStreamFor({
                      fixture_id: m.fixture_id,
                      home: m.home_team.name,
                      away: m.away_team.name,
                    })
                  }
                />
              </div>
            )}

            <div
              onClick={() => navigate(`/live/${m.fixture_id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === "Enter" ? navigate(`/live/${m.fixture_id}`) : null)}
              className="cursor-pointer"
              data-testid={`live-card-body-${m.fixture_id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-display font-semibold truncate flex items-center gap-1.5">
                    <span className="truncate">{m.home_team.name}</span>
                    <span onClick={(e) => e.stopPropagation()} className="shrink-0">
                      <FollowTeamButton teamId={m.home_team.id} teamName={m.home_team.name} size="sm" />
                    </span>
                  </div>
                  <div className="text-sm font-display font-semibold text-muted-foreground truncate mt-1 flex items-center gap-1.5">
                    <span className="truncate">{m.away_team.name}</span>
                    <span onClick={(e) => e.stopPropagation()} className="shrink-0">
                      <FollowTeamButton teamId={m.away_team.id} teamName={m.away_team.name} size="sm" />
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end ml-3">
                  <span className="text-2xl font-display font-bold font-tabular">{m.score.home}</span>
                  <span className="text-2xl font-display font-bold font-tabular text-muted-foreground">
                    {m.score.away}
                  </span>
                </div>
              </div>

              <div className="flex gap-3 mb-3">
                <LMIBar value={m.lmi.home} label="LMI Home" />
                <LMIBar value={m.lmi.away} label="LMI Away" />
              </div>

              {m.in_play_signals?.length > 0 && (
                <div className="space-y-1 pt-2 border-t border-border">
                  {m.in_play_signals.slice(0, 2).map((sig, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-[11px] text-success">
                      <Flame className="w-3 h-3 mt-0.5 shrink-0" />
                      <span>
                        {/* Defensive — backend started emitting structured
                            objects {trigger, condition, ...} alongside the
                            legacy plain strings. Show the trigger label
                            only on the compact card; full details live on
                            the LiveMatchPage. */}
                        {typeof sig === "string" ? sig : sig?.trigger || sig?.message || ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {m.momentum_leader !== "tied" && (
                <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <TrendingUp className="w-3 h-3" />
                  <span>
                    Momentum:{" "}
                    <span className="text-foreground font-bold">
                      {m.momentum_leader === "home" ? m.home_team.name : m.away_team.name}
                    </span>
                  </span>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setStreamFor({
                  fixture_id: m.fixture_id,
                  home: m.home_team.name,
                  away: m.away_team.name,
                });
              }}
              data-testid={`watch-live-btn-${m.fixture_id}`}
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-semibold transition-colors"
            >
              <Tv className="w-3.5 h-3.5" />
              Watch live
            </button>

            <LiveBetButtons match={m} />
          </article>
        ))}
      </div>

      <LiveStreamModal
        open={!!streamFor}
        onOpenChange={(v) => { if (!v) setStreamFor(null); }}
        fixtureId={streamFor?.fixture_id}
        homeTeam={streamFor?.home}
        awayTeam={streamFor?.away}
      />
    </div>
  );
};

export default NowPlayingSection;
